import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDetectionConfig } from './useDetectionConfig'
import { useAudioConfig } from './useAudioConfig'
import { ModelRow } from './ModelRow'
import { DetectorBody } from './DetectorBody'
import { AudioBody } from './AudioBody'

// person/vehicle/animal are structural (always report), not part of the editable species catalog.
// Mirrors SpeciesToggleList / SpeciesCatalogManager so the collapsed count matches those views.
const STRUCTURAL = new Set(['coarse', 'fallback'])

export function DetectionConfig({ cameraId, open, isSuperuser = false }: {
  cameraId: number; open: boolean; isSuperuser?: boolean
}) {
  const { t } = useTranslation('cameras')
  const c = useDetectionConfig(cameraId, open)
  const a = useAudioConfig(cameraId, open && isSuperuser)   // owners never fetch audio (superuser-only endpoint)
  const audioEnabled = a.audioClasses.filter((x) => x.enabled).length
  const visibleDetectors = c.detectors   // owners can now toggle, so they see all active models too
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t('detection.title')}</h3>
        {c.saving || a.saving ? <span className="text-xs text-muted-foreground">{t('detection.saving')}</span>
          : c.saveError || a.saveError ? <span className="text-xs text-amber-600">{t('detection.saveRetry')}</span>
          : <span className="text-xs text-muted-foreground">{t('detection.saved')}</span>}
      </div>
      <p className="text-xs text-muted-foreground">{t('detection.visualHint')}</p>
      {isSuperuser && !c.ensembleEnabled && (
        <p className="text-xs text-amber-600 dark:text-amber-500">{t('detection.ensembleOff')}</p>
      )}
      {c.isLoading ? (
        <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : c.isError ? (
        <div className="flex items-center gap-3 py-2 text-sm text-muted-foreground">
          <span>{t('detection.loadError')}</span>
          <button type="button" onClick={() => c.refetch()} className="text-primary underline">{t('detection.retry')}</button>
        </div>
      ) : visibleDetectors.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">{t('detection.noModels')}</p>
      ) : (
        <div className="space-y-2">
          {visibleDetectors.map((d) => {
            // #135: the open-vocabulary detector reports the whole live species catalog (a species is
            // active unless per-camera disabled), so its count is the catalog size, not the frozen
            // target_classes M2M (active_class_ids). Other models keep their active-selection count.
            const count = d.base_model_type === 'speciesnet-bioclip'
              ? d.available_classes.filter((cls) => !STRUCTURAL.has(cls.category)).length
              : d.active_class_ids.length
            const summary = d.subscribed ? `${count} ${t('detection.classes')}` : t('detection.off')
            return (
              <ModelRow key={d.ai_model_id} title={d.ai_model_name} summary={summary}
                enabled={d.subscribed}
                badge={!d.ai_model_is_active ? t('detection.inactive') : undefined}
                onToggle={(on) => c.patch({ ai_model_id: d.ai_model_id, subscribed: on })}>
                <DetectorBody d={d} patch={c.patch} isSuperuser={isSuperuser} />
              </ModelRow>
            )
          })}
        </div>
      )}

      {isSuperuser && (
        <div className="pt-1">
          <button type="button" disabled={c.resetting}
            onClick={() => { if (window.confirm(t('detection.resetCameraConfirm'))) c.resetCamera() }}
            className="text-xs text-primary underline disabled:opacity-40">
            {t('detection.resetCamera')}
          </button>
        </div>
      )}

      {isSuperuser && (
        <>
          <h4 className="pt-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t('detection.audioGroup')}</h4>
          {a.isLoading ? (
            <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div>
          ) : a.isError ? (
            <div className="flex items-center gap-3 py-2 text-sm text-muted-foreground">
              <span>{t('detection.loadError')}</span>
              <button type="button" onClick={() => a.refetch()} className="text-primary underline">{t('detection.retry')}</button>
            </div>
          ) : a.audioClasses.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">{t('detection.audioNoClasses')}</p>
          ) : (
            <ModelRow title={t('detection.audioGroup')}
              summary={`${audioEnabled}/${a.audioClasses.length} ${t('detection.audioEnabled')}`}
              enabled>
              <AudioBody audioClasses={a.audioClasses} patch={a.patch} />
            </ModelRow>
          )}
        </>
      )}
    </section>
  )
}
